import { type FieldErrors } from 'react-hook-form';
import { type AnyObjectSchema, type ValidationError } from 'yup';

/**
 * Valida campos de forma silenciosa, para no disparar errores visuales en la UI
 * @template T - El tipo de datos del formulario.
 */
export const peekValidation = async <T extends Record<string, unknown>>({
	schema,
	data,
	fields,
}: {
	schema: AnyObjectSchema;
	data: T;
	fields: Array<keyof T>;
}): Promise<boolean> => {
	try {
		if (fields.length === 0) return true;

		// Filtramos para validar SOLO los campos que el esquema conoce
		const validatableFields = fields.filter((field) => field in schema.fields);

		await Promise.all(validatableFields.map((field) => schema.validateAt(field as string, data)));

		return true;
	} catch (error) {
		return false;
	}
};

/**
 * Valida campos de forma silenciosa, para no disparar errores visuales en la UI.
 * Devuelve los errores de las validaciones incorrectas.
 */
export const peekErrors = async <T extends Record<string, unknown>>({
	schema,
	data,
}: {
	schema: AnyObjectSchema;
	data: T;
}): Promise<ValidationError[]> => {
	try {
		await schema.validate(data, { abortEarly: false });
		return [];
	} catch (error) {
		return (error as ValidationError).inner;
	}
};

/**
 * Aplana errores de campos tipo array (ej. documentos).
 * Convierte { input1: err, docs: [err, err] } en { input1: err, docs_0: err, docs_1: err } para facilitar el acceso por índice.
 */
export const flatErrors = <T extends Record<string, unknown>>(obj: FieldErrors<T>) =>
	Object.entries(obj).reduce(
		(acc, [key, value]) => {
			if (Array.isArray(value)) {
				value.flat().forEach((item, index) => {
					acc[`${key}_${index}`] = item;
				});
			} else {
				acc[key] = value;
			}
			return acc;
		},
		// eslint-disable-next-line @typescript-eslint/prefer-reduce-type-parameter
		{} as Record<string, any>,
	);
